/* eslint-disable no-param-reassign */
import { QueryParams, TipoEtapaTrackingEnum, TipoTrackingEnum } from 'config';
import { useFormik } from 'formik';
import { INITIAL_STATE, initValoresRoot, rootContenedorDetalle } from 'helper/tracking.helper';
import { TokenRequestTrackingModel } from 'models';
import { CICLO_FACTURACION, DETALLE, ERROR_N_SERVICIO_NO_EXISTE, ERROR_N_SERVICIO_SIN_INSTANCIA } from 'navigation';
import { useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import TrackingService from 'services/TrackingService';
import { actualizarFacturacion, actualizarLogo, actualizarTrackingCicloComercial, actualizarUsuario, actualizarTramo } from 'store';
import { obtenerLogo } from 'utils';
import { tagFacturacionPantallaIncio, tagFacturacionServicioOK, tagFacturacionValidarNumero } from 'utils/gtmEventsUtils';
import * as Yup from 'yup';

const CicloFacturacion = ({ className }: { className?: string }) => {
  /**
   * useRef de captcha
   */
  let captchaRef: ReCAPTCHA;
  /**
   * useState tipo booleano para mostrar modal
   */
  const [mostrarModal, setMostrarModal] = useState(false);
  /**
   * Valores iniciales vista
   */
  const { history, dispatch, t, styles, cerrarModal, enqueueSnackbar } = initValoresRoot(setMostrarModal);
  let formik: any = initValoresRoot(setMostrarModal);
  /**
   * UseRef de recaptcha
   */
  const setCaptchaRef = (ref: ReCAPTCHA | null) => {
    if (ref) {
      captchaRef = ref;
      return captchaRef;
    }
    return captchaRef;
  };

  /**
   * Variable que maneja query params
   */
  const query = QueryParams();
  /**
   * valor de numero de servicio obtenido por query params
   */
  const queryNumeroServicio = query.get('numeroServicio') || '';
  /**
   * valor de empresa obtenido por query params
   */
  const tipoEmpresa = query.get('empresa');

  /**
   * Metodo que realiza la discriminacion de logo y actualzia logo en redux
   * @param codDistribuidora Codifog de distribuidora
   */
  const validarLogo = (codDistribuidora: string) => {
    const actualizarLogoBody = {
      urlLogo: obtenerLogo(codDistribuidora),
    };
    dispatch(actualizarLogo(actualizarLogoBody));
  };

  /**
   * useEffect principal
   * Devuelve el tramo a valores iniciales al cargar
   */
  useEffect(() => {
    dispatch(actualizarTramo(INITIAL_STATE.dataTramo));
    const data = {
      numero: formik.values.numServicio || queryNumeroServicio,
    };
    tagFacturacionPantallaIncio(data);
    formik.setFieldValue('numServicio', queryNumeroServicio);
    validarLogo('');
    if (tipoEmpresa) {
      validarLogo(tipoEmpresa);
    } else {
      validarLogo('');
    }
  }, []);

  /**
   * Interface de formulario
   */
  interface FormValues {
    numServicio: string;
    captcha: string;
  }

  /**
   * Valores iniciales de formulario
   */
  const initialValues: FormValues = {
    numServicio: '',
    captcha: '',
  };

  const flujoTracking = (estadoCiclo: string) => {
    if (estadoCiclo in TipoEtapaTrackingEnum) {
      history.push(`${CICLO_FACTURACION}/${DETALLE}`);
    } else {
      enqueueSnackbar(t('errors.generic'), {
        variant: 'error',
      });
    }
  };
  /**
   * On submit de formulario
   */
  const onSubmit = async (formValues: any) => {
    const data = {
      numero: formik.values.numServicio,
    };

    const request: TokenRequestTrackingModel = {
      captcha: formValues.captcha,
      nroCliente: formik.values.numServicio,
    };

    try {
      const responseToken = await TrackingService.obtenerTokenTracking(request);
      localStorage.setItem('token', responseToken.token);
      const responseInfoCliente = await TrackingService.obtenerInfoCliente(formik.values.numServicio);

      validarLogo(responseInfoCliente.codDistribuidora);
      dispatch(actualizarUsuario({ ...responseInfoCliente, numeroServicio: formik.values.numServicio }));
      const responseEtiqueta = await TrackingService.obtenerTrackingEtiqueta(formik.values.numServicio);
      const responseEstados = await TrackingService.obtenerEstadosTrackingPorId(responseEtiqueta.trackings[0].id);
      dispatch(actualizarTrackingCicloComercial({ ...responseEstados }));
      const responseFacturacion = await TrackingService.obtenerFacturacionTracking(formik.values.numServicio);
      dispatch(actualizarFacturacion({ ...responseFacturacion }));
      flujoTracking(responseEstados.estado);
      tagFacturacionServicioOK(data);
      formik.resetForm();
      captchaRef.reset();
    } catch (error: any) {
      if (error.message === ERROR_N_SERVICIO_NO_EXISTE) {
        enqueueSnackbar(t('errors.noExisteNumero'), {
          variant: 'error',
        });
      } else if (error.message === ERROR_N_SERVICIO_SIN_INSTANCIA) {
        enqueueSnackbar(t('errors.sinInstancia'), {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(t('errors.generic'), {
          variant: 'error',
        });
      }
      formik.resetForm();
      captchaRef.reset();
    } finally {
      tagFacturacionValidarNumero(data);
    }
  };
  /**
   * Datos de formik
   */
  formik = useFormik({
    initialValues,
    onSubmit,
    validateOnMount: true,
    validationSchema: Yup.object({
      numServicio: Yup.string().required(t('errors.required')).soloNumero(t('errors.soloNumeros')).length(8, t('errors.length')),
      captcha: Yup.string().required(),
    }),
  });

  return rootContenedorDetalle({
    className,
    styles,
    t,
    formik,
    mostrarModal,
    cerrarModal,
    setCaptchaRef,
    setMostrarModal,
    tipoTracking: TipoTrackingEnum.CICLO_FACTURACION,
  });
};

CicloFacturacion.defaultProps = {
  className: '',
};
export default CicloFacturacion;
